import React, { useState, useEffect, useMemo } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { api, generateMockOrders } from '../services/api';
import { parseOrderItems, calculateOrderTotal, formatPrice, cn } from '../lib/utils';
import { motion } from 'framer-motion';
import { useSettings } from '../context/SettingsContext';

const { 
  FiTrendingUp, FiDollarSign, FiShoppingBag, FiActivity, 
  FiLoader, FiPieChart, FiZap, FiStar
} = FiIcons;

const getDates = (period, customRange) => {
  const end = new Date();
  const start = new Date();
  if (period === '7days') start.setDate(start.getDate() - 6);
  else if (period === '30days') start.setDate(start.getDate() - 29);
  else if (period === 'custom') return { start: new Date(new Date(customRange.start).setHours(0,0,0,0)), end: new Date(new Date(customRange.end).setHours(23,59,59,999)) };
  start.setHours(0,0,0,0);
  end.setHours(23,59,59,999);
  return { start, end };
};

const getPreviousDates = (start, end) => {
  const duration = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(prevEnd.getTime() - duration);
  return { start: prevStart, end: prevEnd };
};

const calculateTrend = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Number((((current - previous) / previous) * 100).toFixed(1));
};

const calculateMetrics = (orders) => {
  let revenue = 0;
  let totalItems = 0;
  const itemsMap = {};
  orders.forEach(o => {
    const items = parseOrderItems(o.order);
    revenue += calculateOrderTotal(items);
    totalItems += items.reduce((sum, item) => sum + item.quantity, 0);
    items.forEach(item => {
      if (!itemsMap[item.name]) itemsMap[item.name] = { name: item.name, quantity: 0, revenue: 0 };
      itemsMap[item.name].quantity += item.quantity;
      itemsMap[item.name].revenue += item.totalPrice;
    });
  });
  const topItems = Object.values(itemsMap).sort((a, b) => b.quantity - a.quantity).slice(0, 10);
  const avgOrderValue = orders.length > 0 ? revenue / orders.length : 0;
  return { revenue, ordersCount: orders.length, avgOrderValue, totalItems, topItems };
};

const getChartData = (orders, start, end) => {
  const isSameDay = start.toDateString() === end.toDateString();
  if (isSameDay) {
    const hours = Array.from({ length: 24 }, (_, i) => ({ name: `${String(i).padStart(2, '0')}:00`, revenue: 0, orders: 0 }));
    orders.forEach(o => {
      const d = new Date(o.created_at);
      const items = parseOrderItems(o.order);
      hours[d.getHours()].revenue += calculateOrderTotal(items);
      hours[d.getHours()].orders += 1;
    });
    return hours;
  } else {
    const dates = [];
    let curr = new Date(start);
    curr.setHours(0, 0, 0, 0);
    while (curr <= end) {
      dates.push({ dateStr: `${curr.getFullYear()}-${curr.getMonth()}-${curr.getDate()}`, name: `${curr.getDate()} ${curr.toLocaleString('ru', { month: 'short' })}`, revenue: 0, orders: 0 });
      curr.setDate(curr.getDate() + 1);
    }
    orders.forEach(o => {
      const d = new Date(o.created_at);
      const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      const target = dates.find(x => x.dateStr === key);
      if (target) {
        const items = parseOrderItems(o.order);
        target.revenue += calculateOrderTotal(items);
        target.orders += 1;
      }
    });
    return dates;
  }
};

const StatCard = ({ title, value, subtitle, icon, delay = 0, trend }) => {
  const isPositive = trend > 0;
  const isNegative = trend < 0;
  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay }} className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 relative overflow-hidden group flex flex-col justify-between">
      <div className="relative z-10">
        <div className="flex justify-between items-start mb-2">
          <h3 className="text-xs font-black uppercase tracking-wider text-zinc-500">{title}</h3>
          <div className="p-2 bg-zinc-800 rounded-lg text-yellow-400"><SafeIcon icon={icon} size={16} /></div>
        </div>
        <div className="text-2xl lg:text-3xl font-black text-white mb-2 truncate">{value}</div>
        <div className="flex items-center justify-between">
          {subtitle && <div className="text-[10px] lg:text-xs font-bold text-zinc-500">{subtitle}</div>}
          {trend !== undefined && (
            <div className={cn("flex items-center gap-0.5 text-[10px] lg:text-xs font-black px-2 py-0.5 rounded-full ml-auto", isPositive ? "bg-green-500/10 text-green-400" : isNegative ? "bg-red-500/10 text-red-400" : "bg-zinc-500/20 text-zinc-400")}>
              <SafeIcon icon={isPositive ? FiIcons.FiArrowUpRight : isNegative ? FiIcons.FiArrowDownRight : FiIcons.FiMinus} size={12} />
              {Math.abs(trend)}%
            </div>
          )}
        </div>
      </div>
      <div className="absolute -bottom-4 -right-4 text-zinc-800/30 transform group-hover:scale-110 transition-transform duration-500 pointer-events-none"><SafeIcon icon={icon} size={100} /></div>
    </motion.div>
  );
};

const AnalyticsDashboard = () => {
  const [period, setPeriod] = useState('today');
  const [customRange, setCustomRange] = useState({ start: new Date(new Date().setDate(new Date().getDate() - 7)), end: new Date() });
  const [data, setData] = useState({ currentOrders: [], prevOrders: [], loading: true, error: null });
  const { isTestData } = useSettings();

  const fetchAnalyticsData = async () => {
    setData(prev => ({ ...prev, loading: true, error: null }));
    try {
      const { start, end } = getDates(period, customRange);
      const { start: prevStart, end: prevEnd } = getPreviousDates(start, end);
      let currentOrders = [];
      let prevOrders = [];
      if (isTestData) {
        currentOrders = generateMockOrders(start, end, 50);
        prevOrders = generateMockOrders(prevStart, prevEnd, 45);
      } else {
        [currentOrders, prevOrders] = await Promise.all([api.fetchOrders(start, end), api.fetchOrders(prevStart, prevEnd)]);
      }
      setData({ currentOrders, prevOrders, loading: false, error: null });
    } catch (err) {
      setData(prev => ({ ...prev, loading: false, error: 'Failed to load data' }));
    }
  };

  useEffect(() => { fetchAnalyticsData(); }, [period, customRange, isTestData]);

  const { currentMetrics, prevMetrics, chartData, summaryText } = useMemo(() => {
    const curr = calculateMetrics(data.currentOrders);
    const prev = calculateMetrics(data.prevOrders);
    const { start, end } = getDates(period, customRange);
    
    const revTrend = calculateTrend(curr.revenue, prev.revenue);
    const revWord = revTrend > 0 ? 'выросла' : revTrend < 0 ? 'снизилась' : 'осталась без изменений';
    const topItem = curr.topItems[0];
    const summary = `За период выручка ${formatPrice(curr.revenue)} ₽. В сравнении с прошлым периодом она ${revWord}${revTrend !== 0 ? ` на ${Math.abs(revTrend)}%` : ''}. Продано ${curr.ordersCount} заказов. ${topItem ? `Лидер: «${topItem.name}».` : ''}`;

    return { currentMetrics: curr, prevMetrics: prev, chartData: getChartData(data.currentOrders, start, end), summaryText: summary };
  }, [data, period, customRange]);

  if (data.loading && data.currentOrders.length === 0) {
    return <div className="h-full flex flex-col items-center justify-center text-yellow-400"><SafeIcon icon={FiLoader} className="animate-spin mb-4" size={40} /><p className="text-xl font-bold uppercase tracking-widest">Crunching Numbers...</p></div>;
  }

  return (
    <div className="h-full overflow-y-auto scrollbar-hide pb-20">
      <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 mb-6">
        <h2 className="text-xl lg:text-2xl font-black uppercase tracking-widest text-white flex items-center gap-3"><SafeIcon icon={FiPieChart} className="text-yellow-400" />Analytics</h2>
        <div className="flex flex-col lg:flex-row gap-3">
          <div className="flex bg-zinc-900 rounded-lg p-1 border border-zinc-800">
            {['today', '7days', '30days', 'custom'].map((p) => (
              <button key={p} onClick={() => setPeriod(p)} className={cn("px-4 py-1.5 rounded-md font-bold text-xs lg:text-sm transition-all", period === p ? "bg-zinc-700 text-yellow-400" : "text-zinc-500 hover:text-zinc-300")}>{p === 'today' ? 'Today' : p === '7days' ? '7 Days' : p === '30days' ? '30 Days' : 'Custom'}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Revenue" value={`${formatPrice(currentMetrics.revenue)} ₽`} icon={FiDollarSign} trend={calculateTrend(currentMetrics.revenue, prevMetrics.revenue)} delay={0.1} />
        <StatCard title="Total Orders" value={currentMetrics.ordersCount} icon={FiShoppingBag} trend={calculateTrend(currentMetrics.ordersCount, prevMetrics.ordersCount)} delay={0.2} />
        <StatCard title="Avg Check" value={`${formatPrice(currentMetrics.avgOrderValue)} ₽`} icon={FiTrendingUp} trend={calculateTrend(currentMetrics.avgOrderValue, prevMetrics.avgOrderValue)} delay={0.3} />
        <StatCard title="Items Sold" value={currentMetrics.totalItems} icon={FiActivity} trend={calculateTrend(currentMetrics.totalItems, prevMetrics.totalItems)} delay={0.4} />
      </div>

      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }} className="mb-6 bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 flex gap-4 items-start">
        <div className="p-2 bg-yellow-400/20 rounded-lg text-yellow-400 shrink-0 mt-1"><SafeIcon icon={FiZap} size={20} /></div>
        <div>
          <h3 className="text-sm font-black uppercase text-yellow-500 tracking-wider mb-1">Summary</h3>
          <p className="text-sm lg:text-base text-zinc-300 leading-relaxed font-medium">{summaryText}</p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 mb-6">
        {/* Revenue Trend Chart */}
        <div className="lg:col-span-2 bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-6 min-h-[350px]">
          <h3 className="text-xs font-black uppercase text-zinc-500 mb-6 tracking-widest">Revenue Trend</h3>
          <div className="h-[280px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData}>
                <defs><linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#ecc94b" stopOpacity={0.3}/><stop offset="95%" stopColor="#ecc94b" stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272a" vertical={false} />
                <XAxis dataKey="name" stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#71717a" fontSize={12} tickLine={false} axisLine={false} tickFormatter={(val) => val >= 1000 ? `${(val/1000).toFixed(1)}k` : val} />
                <Tooltip contentStyle={{backgroundColor: '#27272a', border: 'none', borderRadius: '8px'}} />
                <Area type="monotone" dataKey="revenue" stroke="#ecc94b" strokeWidth={3} fillOpacity={1} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top 10 Products */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-6 min-h-[350px] flex flex-col">
          <div className="flex items-center gap-2 mb-6 text-zinc-500">
            <SafeIcon icon={FiStar} className="text-yellow-400" />
            <h3 className="text-xs font-black uppercase tracking-widest">Top 10 Products</h3>
          </div>
          <div className="flex-1 overflow-y-auto scrollbar-hide space-y-4">
            {currentMetrics.topItems.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between group">
                <div className="flex items-center gap-3 overflow-hidden flex-1 pr-4">
                  <div className="w-6 h-6 rounded bg-zinc-800 text-zinc-400 flex items-center justify-center text-[10px] font-black group-hover:bg-yellow-400 group-hover:text-black transition-colors shrink-0">
                    {idx + 1}
                  </div>
                  <div className="text-sm font-bold text-white truncate">{item.name}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-black text-yellow-400">{item.quantity} шт</div>
                  <div className="text-[10px] text-zinc-500 font-bold">{formatPrice(item.revenue)} ₽</div>
                </div>
              </div>
            ))}
            {currentMetrics.topItems.length === 0 && (
              <div className="text-zinc-500 text-sm font-medium text-center py-10">No data available</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;